import { useState } from "react";
import { format, addMonths, subMonths } from "date-fns";
import { es } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { useUserAttendance, AttendanceLog } from "@/hooks/admin/attendance";
import { cn } from "@/lib/utils";

interface UserAttendanceModalProps {
  user: NonNullable<AttendanceLog["user"]>;
  gymId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const UserAttendanceModal = ({
  user,
  gymId,
  isOpen,
  onClose,
}: UserAttendanceModalProps) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();

  const { data: attendanceData, isLoading } = useUserAttendance({
    userId: user.id,
    gymId,
    month: currentMonth,
    enabled: isOpen,
  });

  const handlePrevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
    setSelectedDate(undefined);
  };

  const handleNextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
    setSelectedDate(undefined);
  };

  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
  };

  const getFullName = () => {
    const parts = [user.first_name, user.last_name].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : "Sin nombre";
  };

  const selectedDateKey = selectedDate ? format(selectedDate, "yyyy-MM-dd") : null;
  const selectedDayTimes = selectedDateKey 
    ? attendanceData?.attendanceByDate.get(selectedDateKey) || []
    : [];

  // Custom day render to highlight attendance days
  const modifiers = {
    attended: (date: Date) => {
      const dateKey = format(date, "yyyy-MM-dd");
      return attendanceData?.attendanceDates.has(dateKey) || false;
    },
  };

  const modifiersClassNames = {
    attended: "bg-green-500/30 text-green-400 font-bold hover:bg-green-500/50",
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span>Asistencia de {getFullName()}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="icon" onClick={handlePrevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-medium capitalize">
              {format(currentMonth, "MMMM yyyy", { locale: es })}
            </span>
            <Button variant="ghost" size="icon" onClick={handleNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* Calendar */}
          <div className="flex justify-center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleDateSelect}
              month={currentMonth}
              onMonthChange={setCurrentMonth}
              modifiers={modifiers}
              modifiersClassNames={modifiersClassNames}
              className={cn(
                isLoading && "opacity-50 pointer-events-none"
              )}
            />
          </div>

          {/* Selected day times */}
          {selectedDate && (
            <div className="border-t pt-4">
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {format(selectedDate, "dd 'de' MMMM", { locale: es })}
              </h4>
              
              {selectedDayTimes.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {selectedDayTimes.map((time, idx) => (
                    <div
                      key={idx}
                      className="bg-accent/50 px-3 py-2 rounded text-center text-sm font-medium"
                    >
                      {time.slice(0, 5)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">
                  No hay registros de ingreso para este día
                </p>
              )}
            </div>
          )}

          {/* Summary */}
          <div className="text-sm text-muted-foreground text-center pt-2 border-t">
            {attendanceData?.attendanceDates.size || 0} días de asistencia este mes
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
